import { useRef, useState } from 'react'
import { FileUp, Loader2, Plus } from 'lucide-react'

import { useAppStore } from '@core/store'
import { lerFolhas, type FolhaLida } from '@core/lib/folhas'
import { candidatasAChave, planearImportacao, type PlanoDeImportacao } from '@modules/ifc-edit/lib/importarFolha'
import type { Relatorio } from '@modules/ifc-edit/lib/operacoesEmMassa'
import { depoisDePintar, modeloEmTexto } from './modeloEmTexto'
import { agora, novoIdDeLote, simular } from './simular'
import RelatorioDeOperacoes from './RelatorioDeOperacoes'
import { AVISO, BTN, BTN_PRIMARIO, INPUT, NOTA, ROTULO } from './estilos'

export default function SeparadorImportar({ modelId, aoJuntar }: { modelId: string; aoJuntar: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [nome, setNome] = useState('')
  const [folhas, setFolhas] = useState<FolhaLida[]>([])
  const [folhaIdx, setFolhaIdx] = useState(0)
  const [chave, setChave] = useState<number | undefined>(undefined)
  const [plano, setPlano] = useState<PlanoDeImportacao | null>(null)
  const [simulacao, setSimulacao] = useState<Relatorio | null>(null)
  const [aTrabalhar, setATrabalhar] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const lotes = useAppStore((s) => s.ifcEditLotes)
  const acrescentarLote = useAppStore((s) => s.acrescentarLote)

  const planear = async (fs: FolhaLida[], idx: number, col: number | undefined) => {
    setATrabalhar(true)
    setErro(null)
    setPlano(null)
    setSimulacao(null)
    await depoisDePintar()
    try {
      const m = modeloEmTexto(modelId)
      if (!m) throw new Error('Este modelo não tem o texto do IFC em memória. Abre o .ifc (não o .frag).')
      const folha = fs[idx]
      if (!folha || folha.linhas.length < 2) throw new Error('A folha está vazia ou só tem o cabeçalho.')
      const p = planearImportacao(folha.linhas, m, { chave: col })
      setPlano(p)
      if (p.chave && p.operacoes.length > 0) setSimulacao(simular(modelId, useAppStore.getState().ifcEditLotes, p.operacoes))
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e))
    } finally {
      setATrabalhar(false)
    }
  }

  const abrir = async (f: File) => {
    try {
      const fs = lerFolhas(new Uint8Array(await f.arrayBuffer()), f.name)
      setNome(f.name)
      setFolhas(fs)
      const idx = Math.max(0, fs.findIndex((x) => candidatasAChave(x.linhas[0] ?? []).some((c) => c.tipo === 'guid')))
      setFolhaIdx(idx)
      setChave(undefined)
      void planear(fs, idx, undefined)
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e))
    }
  }

  const juntar = () => {
    if (!plano) return
    acrescentarLote({
      id: novoIdDeLote(),
      descricao: `Importar ${nome}${folhas.length > 1 ? ` (${folhas[folhaIdx]?.nome})` : ''}: ${plano.operacoes.length} valor(es)`,
      quando: agora(),
      operacoes: plano.operacoes,
    })
    useAppStore.getState().pushToast('Importação juntada às alterações pendentes.', { type: 'success' })
    setPlano(null)
    setSimulacao(null)
    setFolhas([])
    setNome('')
    aoJuntar()
  }

  const cabecalho = folhas[folhaIdx]?.linhas[0] ?? []
  const candidatas = candidatasAChave(cabecalho)

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-3 text-[11px]">
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          e.target.value = ''
          if (f) void abrir(f)
        }}
      />
      <p className={NOTA}>
        Uma folha (.xlsx ou .csv) com uma coluna <span className="font-mono">GlobalId</span> e uma coluna por
        propriedade, com o cabeçalho <span className="font-mono">Pset.Propriedade</span> e, se quiseres fixar o tipo,
        <span className="font-mono"> Pset.Propriedade [IfcLengthMeasure]</span>. Colunas sem ponto (Classe, Nome) são só
        para ler. Células vazias não mudam nada. O modelo de preenchimento do separador Inventário já vem neste
        formato.
      </p>
      <details className="text-[10px] text-[var(--color-text-muted)]">
        <summary className="cursor-pointer">Listas, intervalos, quantidades, material e classificação</summary>
        <ul className="mt-1 flex flex-col gap-0.5 pl-3">
          <li>
            <span className="font-mono">Pset.Prop [lista IfcLabel]</span>: lista de valores, separados por{' '}
            <span className="font-mono">|</span>.
          </li>
          <li>
            <span className="font-mono">Pset.Prop [intervalo IfcLengthMeasure]</span>: valores entre limites, escritos{' '}
            <span className="font-mono">min..max</span> (um dos lados pode ficar vazio).
          </li>
          <li>
            <span className="font-mono">Qto_X.Area [IfcQuantityArea]</span>: uma quantidade (não negativa), num conjunto
            de quantidades.
          </li>
          <li>
            <span className="font-mono">Material</span>: o nome de um material simples. Elementos com camadas, perfis
            ou constituintes não se mudam aqui.
          </li>
          <li>
            <span className="font-mono">Classificação: Uniclass 2015</span>: o código, e opcionalmente{' '}
            <span className="font-mono">código | nome</span>. Substitui o código anterior nesse sistema.
          </li>
          <li>A forma só se escolhe para propriedades novas: uma que já existe mantém a forma que tem.</li>
        </ul>
      </details>
      <div className="flex flex-wrap items-center gap-1">
        <button type="button" className={BTN_PRIMARIO} onClick={() => fileRef.current?.click()} disabled={aTrabalhar}>
          <FileUp size={12} /> Escolher folha
        </button>
        {nome && <span className="truncate text-[var(--color-text-muted)]">{nome}</span>}
        {aTrabalhar && <Loader2 size={12} className="animate-spin text-[var(--color-text-muted)]" />}
      </div>
      {erro && <div className={AVISO}>{erro}</div>}

      {folhas.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {folhas.length > 1 && (
            <label className="flex flex-col gap-0.5">
              <span className={ROTULO}>Folha</span>
              <select
                className={INPUT}
                value={folhaIdx}
                onChange={(e) => {
                  const i = Number(e.target.value)
                  setFolhaIdx(i)
                  setChave(undefined)
                  void planear(folhas, i, undefined)
                }}
              >
                {folhas.map((f, i) => (
                  <option key={f.nome} value={i}>{f.nome}</option>
                ))}
              </select>
            </label>
          )}
          <label className="flex flex-col gap-0.5">
            <span className={ROTULO}>Coluna que identifica o elemento</span>
            <select
              className={INPUT}
              value={plano?.chave?.coluna ?? chave ?? ''}
              onChange={(e) => {
                const c = Number(e.target.value)
                setChave(c)
                void planear(folhas, folhaIdx, c)
              }}
            >
              {!plano?.chave && <option value="">(escolher)</option>}
              {candidatas.map((c) => (
                <option key={c.coluna} value={c.coluna}>
                  {cabecalho[c.coluna]}
                  {c.tipo === 'guid' ? ' (GlobalId)' : ' (valor único por elemento)'}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {plano && !plano.chave && (
        <div className={AVISO}>A folha não tem coluna GlobalId. Escolhe uma propriedade cujo valor identifique cada elemento.</div>
      )}

      {plano?.chave && (
        <>
          <div className="text-[10px] text-[var(--color-text-muted)]">
            {plano.colunas.length + plano.associacoes.length} coluna(s) a importar
            {plano.colunas.length + plano.associacoes.length > 0 &&
              `: ${[...plano.colunas, ...plano.associacoes].sort((a, b) => a.coluna - b.coluna).map((c) => c.cabecalho).join(', ')}`}
            .
            {plano.informativas.length > 0 && ` Só para ler: ${plano.informativas.join(', ')}.`}
          </div>
          <div className="text-[10px]">
            <span className="font-medium">{plano.operacoes.length}</span> valor(es) com conteúdo,{' '}
            <span className="font-medium">{plano.alteracoes}</span> diferente(s) do que o ficheiro tem.
          </div>
          {plano.semElemento.length > 0 && (
            <details className="text-[10px]">
              <summary className="cursor-pointer text-[var(--color-warn-text)]">
                {plano.semElemento.length} linha(s) sem elemento correspondente
              </summary>
              <ul className="mt-1 max-h-28 overflow-auto pl-3">
                {plano.semElemento.slice(0, 100).map((s) => (
                  <li key={s.linha}>
                    Linha {s.linha}
                    {s.chave && <span className="font-mono"> {s.chave}</span>}: {s.motivo}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {plano.repetidas.length > 0 && (
            <div className={AVISO}>
              {plano.repetidas.length} elemento(s) aparecem em mais de uma linha: fica o valor da última.
            </div>
          )}
          {plano.amostra.length > 0 && (
            <div className="max-h-40 overflow-auto rounded border border-[var(--color-border)]">
              <table className="w-full text-[10px]">
                <thead className="sticky top-0 bg-[var(--color-panel)] text-left text-[var(--color-text-muted)]">
                  <tr>
                    <th className="px-1">Elemento</th>
                    <th className="px-1">Coluna</th>
                    <th className="px-1">Antes</th>
                    <th className="px-1">Depois</th>
                  </tr>
                </thead>
                <tbody>
                  {plano.amostra.slice(0, 30).map((a, i) => (
                    <tr key={i} className="border-t border-[var(--color-border)]/50">
                      <td className="px-1 font-mono">{a.guid}</td>
                      <td className="px-1">{a.rotulo}</td>
                      <td className="px-1 text-[var(--color-text-muted)]">{a.antes ?? '(não existe)'}</td>
                      <td className="px-1">{a.depois}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {simulacao && (
            <>
              <div className="mt-1 text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">Simulação</div>
              <RelatorioDeOperacoes r={simulacao} />
            </>
          )}
          <div className="flex gap-1">
            <button type="button" className={BTN_PRIMARIO} onClick={juntar} disabled={plano.operacoes.length === 0}>
              <Plus size={12} /> Juntar às alterações
            </button>
            <button type="button" className={BTN} onClick={() => { setPlano(null); setSimulacao(null); setFolhas([]); setNome('') }}>
              Cancelar
            </button>
          </div>
          <p className={NOTA}>
            Nada se grava ainda: as alterações ficam pendentes (há {lotes.length} lote(s) antes deste) e escrevem-se no
            separador Alterações.
          </p>
        </>
      )}
    </div>
  )
}
